<template>
  <div
    ref="menuRef"
    v-mousedown-outside="close"
    v-click-outside="close"
    v-wheel-outside="close"
    class="context-menu minimalistic minimalistic-thin"
    :style="`top: ${positionWithOffset.y}px; left: ${positionWithOffset.x}px; max-height: ${maxHeight}; max-width: ${width}; min-width: ${width}`"
    @wheel="handleWheel"
  >
    <div v-if="slots.sticky" class="sticky-top bg-white p-2">
      <slot name="sticky" />
    </div>

    <slot />

    <template v-for="(option, i) of filteredOptions" :key="i">
      <div class="d-flex w-100">
        <button
          v-if="option.type === 'action'"
          class="option"
          :class="{ 'selected-index': selectedIndex === i }"
          :disabled="option.disabled"
          @mouseenter="selectedIndex = i"
          @click="selectOption(option)"
        >
          <i class="icon fa" :class="option.icon" />
          <span v-html="option.label" />
          <span class="ml-auto">{{ option.keyboardShortcut }}</span>

          <i v-if="option.opensPrompt" class="fa fa-external-link" />
          <InfoPopupComponent v-if="option.info" class="ml-1" :content-style="'right: 10px'">{{ option.info }}</InfoPopupComponent>
        </button>

        <button
          v-if="option.collapsedItems?.length"
          class="option expand"
          :disabled="option.disabled"
          @mouseenter="selectedIndex = undefined"
          @click="toggleSubmenu(i)"
        >
          <i class="fa fa-angle-down" />
        </button>
      </div>
      <div v-if="showCollapsed.has(i)" class="w-100 inner-options">
        <button
          v-for="(innerOption, j) of option.collapsedItems"
          :key="j"
          class="option inner w-100"
          :disabled="option.disabled || innerOption.disabled"
          @mouseenter="selectedIndex = undefined"
          @click="selectOption(innerOption)"
        >
          <i class="icon fa" :class="innerOption.icon" />
          <span v-html="innerOption.label" />
          <div class="ml-auto">
            <!-- <InfoPopupComponent v-if="innerOption.info" class="ml-auto" :content-style="'right: 10px'">{{ innerOption.info }}</InfoPopupComponent> -->

            <TooltipComponent
              v-if="innerOption.info"
              :tooltip-style="{ zIndex: tooltipZIndex, width: 'max-content', transform: 'translate(calc(-100% - 10px))' }"
            >
              <template #trigger>
                <i class="fa fa-info-circle" />
              </template>

              {{ innerOption.info }}
            </TooltipComponent>
          </div>
        </button>
      </div>

      <div v-else-if="option.type === 'separator'" class="separator" />
    </template>
  </div>
</template>

<script setup lang="ts">
import { ContextMenuOption, BasicContextMenuOption } from '@/models/ContextMenu'
import { Coord } from '@/models/GenericInterfaces'
import { computed, nextTick, onMounted, onUnmounted, ref, useSlots } from 'vue'
import InfoPopupComponent from '@/components/simple/InfoPopupComponent.vue'
import TooltipComponent from './simple/TooltipComponent.vue'

interface Props {
  position: Coord
  maxHeight?: string
  width?: string
  options: ContextMenuOption[]
  marginLeftPx?: number
}

const tooltipZIndex = 10001

// -----------
// -- Setup --
// -----------
const props = withDefaults(defineProps<Props>(), {
  maxHeight: '800px',
  width: '300px',
  marginLeftPx: 0,
})

const emit = defineEmits(['close'])

const slots = useSlots()

// ---------------
// -- Reactives --
// ---------------
const menuRef = ref<HTMLElement>()
const selectedIndex = ref<number>()

const showCollapsed = ref<Set<number>>(new Set())

const positionKey = ref(0)

const positionWithOffset = computed(() => {
  positionKey.value

  const rect = menuRef.value?.getBoundingClientRect()

  if (rect) {
    const xOffset = props.position.x + rect.width + props.marginLeftPx > window.innerWidth ? -rect.width : 0
    const yOffset = props.position.y + rect.height > window.innerHeight ? -rect.height : 0

    const smallScreenLeftMargin = 5
    return {
      x: rect.width * 2 > window.innerWidth ? smallScreenLeftMargin : props.position.x + xOffset, // set x position. use a static position for small screens to make the menu fit
      y: props.position.y + yOffset,
    }
  }
  return props.position
})

// ---------------------
// -- Lifecycle hooks --
// ---------------------
// onMounted(() => {
//   document.addEventListener(
//     'keydown',
//     (e: KeyboardEvent) => {
//       if (e.key === 'Escape') {
//         close()
//       }
//     },
//     { once: true },
//   )
// })

onMounted(() => document.addEventListener('keydown', keydownHandler))
onUnmounted(() => document.removeEventListener('keydown', keydownHandler))

// -------------
// -- Methods --
// -------------
const keydownHandler = (e: KeyboardEvent) => {
  const getCloestAction = (index: number, deltaChange: number) => {
    do {
      index += deltaChange
    } while (filteredOptions.value[index]?.type === 'separator' || filteredOptions.value[index]?.disabled)
    return index
  }
  const maxValue = filteredOptions.value.length - 1

  if (e.key === 'Escape') {
    close()
  } else if (e.key === 'ArrowUp') {
    e.stopPropagation()
    e.preventDefault()

    if (selectedIndex.value === undefined) {
      selectedIndex.value = maxValue
    } else {
      selectedIndex.value = Math.max(0, getCloestAction(selectedIndex.value || 0, -1))
    }
  } else if (e.key === 'ArrowDown') {
    e.stopPropagation()
    e.preventDefault()

    if (selectedIndex.value === undefined) {
      selectedIndex.value = 0
    } else {
      selectedIndex.value = Math.min(maxValue, getCloestAction(selectedIndex.value, 1))
    }
  } else if (e.key === 'Enter' && selectedIndex.value !== undefined) {
    e.stopPropagation()
    selectOption(filteredOptions.value[selectedIndex.value])
  }
}

const close = (e?: any) => {
  if (!e || e.button === 0) {
    emit('close')
  }
}

const selectOption = (option: BasicContextMenuOption) => {
  option.callback?.()
  close()
}

const filteredOptions = computed(() => props.options?.filter((option) => !option.hidden))

const toggleSubmenu = (optionIndex: number) => {
  if (showCollapsed.value.has(optionIndex)) {
    showCollapsed.value.delete(optionIndex)
  } else {
    showCollapsed.value.add(optionIndex)
  }

  nextTick().then(() => {
    positionKey.value++
  })
}

const handleWheel = (e: WheelEvent) => {
  if (e.deltaX !== 0) {
    e.preventDefault()
  }
}
</script>

<style lang="scss" scoped>
.context-menu {
  $icon-width: 2em;

  outline: none;
  font-size: 12.3px;
  position: fixed;
  z-index: 10000;
  display: flex;
  flex-wrap: wrap;

  background-color: #fff;
  border: 1px solid #eee;

  user-select: none;

  box-shadow: 0 5px 17px -1px #0003;

  overflow-y: auto;

  border-radius: 10px;
  padding: 10px 0;

  overscroll-behavior: contain;

  button.option {
    display: flex;
    width: 100%;
    align-items: center;
    outline: none;
    cursor: default;
    padding: 0.4em 1.8em;
    border: none;
    text-align: left;
    background-color: transparent;
    color: black;

    &.selected-index:not(:disabled) {
      background-color: #eee;
    }

    .icon {
      min-width: $icon-width;
      max-width: $icon-width;
      margin-left: 3px;
      margin-right: 2px;
    }

    &.expand {
      $sidePadding: 2em;
      padding-left: $sidePadding;
      padding-right: $sidePadding;
      width: unset;
    }

    &.expand,
    &.inner {
      &:hover:not(:disabled) {
        background-color: #eee;
      }
    }

    &.inner {
      // text-indent: 1.25em;
    }
  }

  .inner-options {
    background-color: #f8f8f8;
  }

  .separator {
    border-top: 1px solid #ddd;
    margin: 0.5em 0;
    width: 100%;
  }
}
</style>
